/**
 * Mathematical and UI constants used throughout the application
 * This file centralizes all "magic numbers" to improve code maintainability
 */

// Mathematical constants
export const MATH_CONSTANTS = {
    // Angle conversions
    DEGREES_TO_RADIANS: Math.PI / 180,
    RADIANS_TO_DEGREES: 180 / Math.PI,
    HALF_PI: Math.PI / 2,
    QUARTER_PI: Math.PI / 4,
    TWO_PI: Math.PI * 2,

    // Common angles in radians
    ANGLE_90_DEGREES: Math.PI / 2,
    ANGLE_180_DEGREES: Math.PI,
    ANGLE_270_DEGREES: (3 * Math.PI) / 2,
    ANGLE_360_DEGREES: 2 * Math.PI,

    // Precision and tolerance
    ANGLE_TOLERANCE: 0.01, // Minimum angle difference to consider significant
    COORDINATE_PRECISION: 2, // Decimal places for coordinate rounding
    CALCULATION_EPSILON: 1e-10, // Small value for floating point comparisons
};

// Canvas and interaction constants
export const CANVAS_CONSTANTS = {
    // Zoom settings
    ZOOM_IN_FACTOR: 1.1, // Factor for zooming in
    ZOOM_OUT_FACTOR: 0.9, // Factor for zooming out
    MIN_ZOOM: 0.1, // Minimum zoom level
    MAX_ZOOM: 10.0, // Maximum zoom level
    ZOOM_SENSITIVITY: 0.1, // Mouse wheel zoom sensitivity
    PINCH_ZOOM_SENSITIVITY: 0.01, // Touch pinch zoom sensitivity

    // Pan settings
    MAX_PAN_DISTANCE: 2000, // Maximum pan distance from origin

    // Touch handling
    SINGLE_TOUCH: 1, // Number of touches for single touch
    MULTI_TOUCH: 2, // Number of touches for pinch/zoom
    TOUCH_TOLERANCE: 5, // Minimum touch movement to trigger drag

    // Animation and timing
    ANIMATION_DELAY_MS: 50, // Small delay for CSS transitions
    LEVEL_DELAY_MS: 500, // Delay between fractal levels in animation
    POLYGON_DELAY_MS: 100, // Delay between polygons within level

    // UI dimensions
    MOBILE_BREAKPOINT: 767, // Screen width considered mobile
    PANEL_Z_INDEX: 100, // Z-index for control panel
    TOGGLE_BUTTON_Z_INDEX: 101, // Z-index for panel toggle button (higher than panel)
};

// Fractal generation constants
export const FRACTAL_CONSTANTS = {
    // Default geometry
    DEFAULT_TRIANGLE_ANGLE: 45, // Default triangle angle in degrees
    DEFAULT_BASE_SQUARE_SIZE: 100, // Default starting square size in pixels
    DEFAULT_RECURSION_DEPTH: 7, // Default maximum tree depth

    // Limits and ranges
    MIN_TRIANGLE_ANGLE: 10, // Minimum triangle angle in degrees
    MAX_TRIANGLE_ANGLE: 80, // Maximum triangle angle in degrees
    MIN_RECURSION_DEPTH: 1, // Minimum recursion depth
    MAX_RECURSION_DEPTH: 16, // Maximum recursion depth
    MIN_SQUARE_SIZE: 1, // Minimum square size
    MAX_SQUARE_SIZE: 500, // Maximum square size

    // Animation speed
    MIN_ANIMATION_SPEED: 0.1, // Minimum animation speed multiplier
    MAX_ANIMATION_SPEED: 5.0, // Maximum animation speed multiplier
    DEFAULT_ANIMATION_SPEED: 1.0, // Default animation speed multiplier

    // Geometric calculations
    TRIANGLE_HEIGHT_RATIO: 0.5, // Ratio for triangle height calculations
    SQUARE_CENTER_OFFSET_RATIO: 0.5, // Ratio for positioning squares on triangle sides
};

// Export format constants
export const EXPORT_CONSTANTS = {
    // PNG export settings
    PNG: {
        DEFAULT_WIDTH: 2048, // Default PNG export width
        DEFAULT_HEIGHT: 2048, // Default PNG export height
        DEFAULT_QUALITY: 1.0, // Default PNG quality (0.0-1.0)
        MAX_QUALITY: 1.0, // Maximum PNG quality
        MIN_QUALITY: 0.1, // Minimum PNG quality
    },

    // SVG export settings
    SVG: {
        DEFAULT_WIDTH: 1024, // Default SVG viewport width
        DEFAULT_HEIGHT: 1024, // Default SVG viewport height
        COORDINATE_PRECISION: 2, // SVG coordinate decimal precision
        DEFAULT_PADDING: 50, // Default padding around tree in SVG
        OPTIMIZATION_THRESHOLD: 0.01, // Threshold for SVG optimization
    },

    // Export presets
    PRESETS: {
        WEB: { width: 1024, height: 1024, quality: 0.9 },
        HD: { width: 1920, height: 1920, quality: 1.0 },
        UHD: { width: 3840, height: 3840, quality: 1.0 },
        PRINT: { width: 4096, height: 4096, quality: 1.0 },
    },
};

// Color system constants
export const COLOR_CONSTANTS = {
    // Default colors
    DEFAULT_PRIMARY_COLOR: '#4CAF50', // Default primary color
    DEFAULT_SECONDARY_COLOR: '#2196F3', // Default secondary color
    DEFAULT_BACKGROUND_COLOR: '#FFFFFF', // Default background color

    // Color pattern calculations
    HUE_INFLUENCE_FACTOR: 0.3, // Factor for user color influence on hue
    SATURATION_BASE: 70, // Base saturation value
    SATURATION_FACTOR: 0.7, // Factor for saturation calculations
    SATURATION_OFFSET: 30, // Offset for saturation calculations

    // HSV ranges
    HUE_MAX: 360, // Maximum hue value
    SATURATION_MAX: 100, // Maximum saturation value
    VALUE_MAX: 100, // Maximum value (brightness)
};

// Keyboard shortcuts
export const KEYBOARD_CONSTANTS = {
    // Preset keys
    PRESET_KEY_1: '1',
    PRESET_KEY_2: '2',
    PRESET_KEY_3: '3',
    PRESET_KEY_4: '4',
    PRESET_KEY_5: '5',
    PRESET_KEY_6: '6',

    // Preset indices
    PRESET_INDEX_1: 0,
    PRESET_INDEX_2: 1,
    PRESET_INDEX_3: 2,
    PRESET_INDEX_4: 3,
    PRESET_INDEX_5: 4,
    PRESET_INDEX_6: 5,
};

// Layout and responsive design constants
export const LAYOUT_CONSTANTS = {
    // Padding and margins
    DEFAULT_PADDING: 20, // Default padding for test pages
    SECTION_MARGIN: 20, // Margin for test sections
    ELEMENT_SPACING: 10, // Standard spacing between elements

    // Button dimensions
    BUTTON_PADDING_VERTICAL: 10, // Button vertical padding
    BUTTON_PADDING_HORIZONTAL: 20, // Button horizontal padding
    BUTTON_BORDER_RADIUS: 4, // Button border radius

    // Test output dimensions
    TEST_OUTPUT_MAX_HEIGHT: 300, // Maximum height for test output areas
    LOG_ITEM_PADDING: 5, // Padding for log items

    // Border and visual elements
    BORDER_WIDTH: 1, // Standard border width
    BORDER_RADIUS: 4, // Standard border radius
    LARGE_BORDER_RADIUS: 8, // Larger border radius for containers
};

// Performance and optimization constants
export const PERFORMANCE_CONSTANTS = {
    // Rendering thresholds
    MAX_VISIBLE_LEVELS: 20, // Maximum levels to render for performance
    OPTIMIZATION_LEVEL_THRESHOLD: 10, // Level at which to start optimizations

    // Memory management
    MAX_CACHED_FRAMES: 50, // Maximum cached animation frames
    CLEANUP_INTERVAL_MS: 5000, // Interval for memory cleanup

    // Update intervals
    UI_UPDATE_INTERVAL_MS: 16, // ~60fps for UI updates
    CANVAS_REDRAW_THROTTLE_MS: 33, // ~30fps for canvas redraws
    UI_TIMEOUT_MS: 100, // Standard UI timeout for state changes
};
